import React from 'react'
import PropTypes from 'prop-types'
import {
  BulletList,
  CodeBlock,
  CodeLabel,
  DiagramCaption,
  DiagramFigure,
  GalleryCaption,
  GalleryFigure,
  GalleryGrid,
  MetricTag,
  MetricTags,
  Section,
  SectionBody,
  SectionTitle,
  TechCategory,
  TechCategoryLabel,
  TechCategoryList,
  TechGrid,
  TechItemTag,
  TimelineBlock,
  TimelineDescription,
  TimelineList,
  TimelineTitle,
} from './ProjectDetail.styles'

const TextSection = ({ section }) => (
  <Section>
    {section.title && <SectionTitle>{section.title}</SectionTitle>}
    <SectionBody>{section.content}</SectionBody>
  </Section>
)

const ListSection = ({ section }) => (
  <Section>
    {section.title && <SectionTitle>{section.title}</SectionTitle>}
    <BulletList>
      {section.content.map((item, index) => (
        <li key={`${section.id}-item-${index}`}>{item}</li>
      ))}
    </BulletList>
  </Section>
)

const DiagramSection = ({ section }) => {
  const { caption, image } = section.content

  return (
    <Section>
      {section.title && <SectionTitle>{section.title}</SectionTitle>}
      <DiagramFigure>
        <img alt={section.title || caption || 'Diagram'} src={image} />
        {caption && <DiagramCaption>{caption}</DiagramCaption>}
      </DiagramFigure>
    </Section>
  )
}

const GallerySection = ({ section }) => (
  <Section>
    {section.title && <SectionTitle>{section.title}</SectionTitle>}
    <GalleryGrid>
      {section.content.map((item, index) => (
        <GalleryFigure key={`${section.id}-gallery-${index}`}>
          <img alt={item.caption || section.title || 'Screenshot'} src={item.image} />
          {item.caption && <GalleryCaption>{item.caption}</GalleryCaption>}
        </GalleryFigure>
      ))}
    </GalleryGrid>
  </Section>
)

const TimelineSection = ({ section }) => (
  <Section>
    {section.title && <SectionTitle>{section.title}</SectionTitle>}
    <TimelineList>
      {section.content.map((item, index) => (
        <TimelineBlock key={`${section.id}-timeline-${index}`}>
          {item.title && <TimelineTitle>{item.title}</TimelineTitle>}
          {item.description && <TimelineDescription>{item.description}</TimelineDescription>}
        </TimelineBlock>
      ))}
    </TimelineList>
  </Section>
)

const TechnologiesSection = ({ section }) => {
  const grouped = section.content.reduce((acc, item) => {
    const category = item.category || 'general'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(item.name)
    return acc
  }, {})

  const categories = Object.keys(grouped)
  const isSingleGroup = categories.length === 1 && categories[0] === 'general'

  return (
    <Section>
      {section.title && <SectionTitle>{section.title}</SectionTitle>}
      {isSingleGroup ? (
        <TechGrid>
          {grouped.general.map((name) => (
            <TechItemTag key={name}>{name}</TechItemTag>
          ))}
        </TechGrid>
      ) : (
        <TechCategoryList>
          {categories.map((category) => (
            <TechCategory key={category}>
              <TechCategoryLabel>{category}</TechCategoryLabel>
              <TechGrid>
                {grouped[category].map((name) => (
                  <TechItemTag key={`${category}-${name}`}>{name}</TechItemTag>
                ))}
              </TechGrid>
            </TechCategory>
          ))}
        </TechCategoryList>
      )}
    </Section>
  )
}

const MetricsSection = ({ section }) => (
  <Section>
    {section.title && <SectionTitle>{section.title}</SectionTitle>}
    <MetricTags>
      {section.content.map((item, index) => (
        <MetricTag key={`${section.id}-metric-${index}`}>{item}</MetricTag>
      ))}
    </MetricTags>
  </Section>
)

const CodeSection = ({ section }) => {
  const { code, language } = section.content

  return (
    <Section>
      {section.title && <SectionTitle>{section.title}</SectionTitle>}
      {language && <CodeLabel>{language}</CodeLabel>}
      <CodeBlock>
        <code>{code}</code>
      </CodeBlock>
    </Section>
  )
}

const SECTION_RENDERERS = {
  text: TextSection,
  list: ListSection,
  diagram: DiagramSection,
  gallery: GallerySection,
  timeline: TimelineSection,
  technologies: TechnologiesSection,
  metrics: MetricsSection,
  code: CodeSection,
}

const CaseStudySections = ({ sections = [] }) => {
  if (!sections.length) {
    return null
  }

  return sections.map((section) => {
    const Renderer = SECTION_RENDERERS[section.type]

    if (!Renderer) {
      return null
    }

    return <Renderer key={section.id} section={section} />
  })
}

CaseStudySections.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      title: PropTypes.string,
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
    }),
  ),
}

export default CaseStudySections
